import React from 'react'
import { imageUrl, srcSet } from '../lib/images'

export default function Hero() {
  const heroImage = '/_assets/bft-global-hero-plant.jpg'

  const coreFocusAreas = [
    { name: 'Biofuel', icon: 'fa-solid fa-gas-pump', desc: 'Advanced renewable liquid fuels' },
    { name: 'Biomass processing', icon: 'fa-solid fa-recycle', desc: 'Silvicultural & agricultural feedstocks' },
    { name: 'Biogas', icon: 'fa-solid fa-fire-flame-simple', desc: 'High-yield biomethane production' },
    { name: 'Green chemicals', icon: 'fa-solid fa-flask-vial', desc: 'Sustainable bio-based chemical building blocks' },
    { name: 'Biogenic CO2', icon: 'fa-solid fa-seedling', desc: 'Circular carbon capture & utilization' },
    { name: 'Sustainable industrial technology', icon: 'fa-solid fa-industry', desc: 'Modern high-efficiency conversion systems' },
  ]

  return (
    <section id="top" className="relative pt-24 pb-12 sm:pt-32 sm:pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
      {/* Background ambient accents - subtle, professional Scandinavian industrial feel */}
      <div
        className="pointer-events-none absolute -top-40 right-1/4 w-[600px] h-[600px] rounded-full bg-emerald-900/10 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute top-1/3 -left-20 w-[450px] h-[450px] rounded-full bg-[#1b3424]/20 blur-3xl"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Top Badges */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#122419] border border-[#1e4231] text-xs font-semibold tracking-wide text-emerald-300">
            <span className="w-2 h-2 rounded-full bg-emerald-400" aria-hidden="true" />
            <span>TAB HOLDING ApS</span>
          </div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-900/90 border border-amber-600/40 text-xs font-semibold text-amber-200">
            <span className="w-2 h-2 rounded-full bg-amber-400" aria-hidden="true" />
            <span>Midlertidig hjemmeside · Under etablering</span>
          </div>
        </div>

        {/* Main Hero Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-emerald-400 mb-3">
              Official Corporate Presentation
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.12]">
              Biofuel Technology Global
            </h1>
            <p className="mt-3 text-lg sm:text-2xl font-medium text-emerald-200/90 tracking-tight">
              BFT Global
            </p>

            <p className="mt-6 text-base sm:text-lg text-neutral-300 leading-relaxed font-normal max-w-2xl">
              Specialized in the collection, preprocessing, and advanced technological processing of
              lignocellulosic biomass into sustainable biofuels, biogasses, green chemicals, and biogenic CO2.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#announcement"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold text-sm text-neutral-950 bg-emerald-400 hover:bg-emerald-300 transition-colors shadow-lg shadow-emerald-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                <span>Read Announcement</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold text-sm text-white bg-[#14291c] hover:bg-[#1b3826] border border-[#234b33] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                <i className="fa-solid fa-address-card text-emerald-400 text-sm" aria-hidden="true" />
                <span>Contact Management</span>
              </a>
            </div>
          </div>

          {/* Visual Element: Scandinavian Industrial Biorefinery Installation */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-[#20442f] bg-[#0f2116] shadow-2xl">
              <div className="aspect-[4/3] w-full overflow-hidden relative">
                <img
                  src={imageUrl(heroImage, 'width=1000,quality=85,format=auto')}
                  srcSet={srcSet(heroImage, [500, 800, 1000])}
                  sizes="(min-width: 1024px) 40vw, 92vw"
                  alt="Biofuel Technology Global - Industrial lignocellulosic biomass processing facility"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09150d] via-transparent to-transparent opacity-80" />
              </div>
              
              <div className="p-4 sm:p-5 bg-[#0e1f14] border-t border-[#1b3826]">
                <div className="flex items-center justify-between text-xs text-neutral-400">
                  <span className="font-semibold text-emerald-400 flex items-center gap-1.5">
                    <i className="fa-solid fa-industry text-xs" aria-hidden="true" />
                    Industrial Bioenergy Plant
                  </span>
                  <span className="text-neutral-400 text-[11px]">BFT Global Technology</span>
                </div>
                <p className="mt-1 text-xs text-neutral-300 font-normal">
                  Conversion of lignocellulosic silvicultural & agricultural biomass to clean energy and biogenic CO2.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 6 Key Core Focus Area Signals */}
        <div className="mt-10 pt-8 border-t border-[#1a3324]">
          <div className="text-center sm:text-left mb-6">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-400">
              Core Strategic Focus Areas
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {coreFocusAreas.map((item) => (
              <div
                key={item.name}
                className="bg-[#0e1a12] border border-[#1b3423] rounded-xl p-3.5 sm:p-4 flex flex-col justify-between"
              >
                <div className="w-8 h-8 rounded-lg bg-[#14281c] border border-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-sm`} aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white leading-tight">
                    {item.name}
                  </h2>
                  <p className="mt-1 text-[11px] text-neutral-400 leading-tight">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
